import React from 'react'

const hasClipboardAPI = (): boolean => Boolean(navigator.clipboard)
const withClipboardAPI = (text: string) => navigator.clipboard.writeText(text)
// eslint-disable-next-line require-await
const withClipboardFallbackAPI = async (text: string): Promise<void> => {
    const span = document.createElement('span')
    span.textContent = text

    span.style.whiteSpace = 'pre'
    span.style.userSelect = 'all'

    document.body.appendChild(span)

    const selection = window.getSelection()!
    const range = window.document.createRange()
    selection.removeAllRanges()
    range.selectNode(span)
    selection.addRange(range)

    // Copy text to the clipboard
    let success = false
    try {
        success = window.document.execCommand('copy')
    } finally {
        // Cleanup
        selection.removeAllRanges()
        window.document.body.removeChild(span)
    }
    if (!success) {
        console.info('failed to copy')
    }
}


const copyToClipboard = async (text: string): Promise<void> => {
    if (hasClipboardAPI()) {
        await withClipboardAPI(text)
        return
    }
    await withClipboardFallbackAPI(text)
}

export type ClipboardOptions = {
    onSuccess?: (key?: string) => void;
    onError?: () => void;
}

const useCopyToClipboard = (options: ClipboardOptions) => {
    const targetRef = React.useRef<HTMLTextAreaElement | HTMLInputElement>(null)
    const optionsRef = React.useRef<ClipboardOptions>(options)
    const copyHandler = React.useCallback((text?: unknown, key?: string) => {
        const opts = optionsRef.current
        const target = targetRef.current
        if (typeof text === 'string') {
            copyToClipboard(text).then(() => opts.onSuccess && opts.onSuccess(key))
                .catch(opts.onError)
        } else if (!isNaN(<number>text)) {
            copyToClipboard(`${text}`).then(() => opts.onSuccess && opts.onSuccess(key))
                .catch(opts.onError)
        } else if (typeof text === 'object') {
            copyToClipboard(JSON.stringify(text)).then(() => opts.onSuccess && opts.onSuccess(key))
                .catch(opts.onError)
        } else if (target) {
            copyToClipboard(target.value).then(() => opts.onSuccess && opts.onSuccess(key))
                .catch(opts.onError)
        }
    }, [])

    return {
        copy: copyHandler,
        target: targetRef,
    }
}

export {useCopyToClipboard}
